<!-- Empty Log Template -->
<ng-template #empty_log_template>
  <span>...</span>
</ng-template>

<!-- Details View - Modal Popup -->
<ng-template #log_template
  let-dynamicDialogConfig="dynamicDialogConfig">
  @if (dynamicDialogConfig.data.statsData; as statsData) {
    <div
      class="flex flex-column row-gap-2">
      <div class="flex flex-row gap-1">
        <p-tag 
          [value]="statsData.profile || undefined" 
          severity="info" />
        <p-tag 
          [value]="`enum.importStatus.${statsData.status}` | translate"
          [severity]="getStatusSeverityClass(statsData.status)" />
      </div>
      <div class="flex flex-1 flex-column surface-ground p-3 h-full"
        style="font-family: monospace;">
        @if (statsData.logs.length > 0) {
          @for (log of statsData.logs | sortBy_shared: 'asc':'ts'; track log) {
            <div
              class="flex">
              <span class="text-300 pr-3">{{ log.ts | date: 'HH:mm:ss' }}</span>
              {{ getM(log) }}
            </div>
          }
        } @else {
          <span>...</span>
        }
      </div>
    </div>
  }
</ng-template>

<div class="flex flex-column w-full h-screen min-h-screen">
  <!-- Navigation -->
  <medsurf-navbar></medsurf-navbar>
  <!-- Toolbar -->
  <medsurf-design-content-toolbar [class]="'mb-2'">
    <!-- Content -->
    <div class="p-toolbar-group-start">
      &nbsp;
    </div>
    <div class="p-toolbar-group-end">
      @if ({
        canRedo: historySharedFacade.canRedo$ | async,
        canUndo: historySharedFacade.canUndo$ | async
      }; as historyData) {
        <div
          class="flex flex-row flex-wrap align-items-center gap-2">
          <button medsurf-design-input-button [id]="'undo'"
            [title]="'global.undo' | translate"
            [inputClass]="FlatModels.ButtonDesignModels.ButtonDesignClass.PRIMARY"
            [iconClass]="FlatModels.ButtonDesignModels.ButtonDefaultIcons.UNDO"
            [disabled]="historyData.canUndo === false"
          (click)="executeUndo()"></button>
          <button medsurf-design-input-button [id]="'redo'"
            [title]="'global.redo' | translate"
            [inputClass]="FlatModels.ButtonDesignModels.ButtonDesignClass.PRIMARY"
            [iconClass]="FlatModels.ButtonDesignModels.ButtonDefaultIcons.REDO"
            [disabled]="historyData.canRedo === false"
          (click)="executeRedo()"></button>
          @if ({
            canSave: historySharedFacade.canSave$ | async
          }; as saveData) {
            <button medsurf-design-input-button [id]="'save_changes'"
              [label]="'global.save_changes' | translate"
              [inputClass]="(saveData.canSave === false ? FlatModels.ButtonDesignModels.ButtonDesignClass.PRIMARY : FlatModels.ButtonDesignModels.ButtonDesignClass.WARNING)"
              [iconClass]="FlatModels.ButtonDesignModels.ButtonDefaultIcons.SAVE"
              [disabled]="saveData.canSave === false"
            (click)="executeSave()"></button>
            @if (slideAuthorFacade.currentSelectedRouteElement$ | async; as routeElement) {
              <button medsurf-design-input-button
                [id]="'preview'"
                [label]="'global.preview' | translate"
                [inputClass]="(saveData.canSave === false ? FlatModels.ButtonDesignModels.ButtonDesignClass.PRIMARY : FlatModels.ButtonDesignModels.ButtonDesignClass.WARNING)"
                [iconClass]="FlatModels.ButtonDesignModels.ButtonDefaultIcons.PREVIEW"
              (click)="openNodePreview(routeElement)"></button>
            }
          }
        </div>
      }
    </div>
  </medsurf-design-content-toolbar>
  <!-- Search Results -->
  <div class="w-full h-full overflow-hidden">
    <!-- Data View -->
    @let layout = (slideAuthorFacade.layout$ | async) ?? 'list';
    @if (filteredScans$ | async; as filteredScans) {
      <medsurf-design-content-sortable-data-view
        [value]="filteredScans"
        [gridStyleClass]="'flex flex-wrap gap-2 p-2'"
        [listStyleClass]="'p-2'"
        [paginatorDropdownScrollHeight]="'300px'"
        [trackBy]="trackBy"
        [rows]="toolbarSettingsForm.value.rows || 10"
        [layout]="layout"
        (onChangeLayout)="changeLayout($event)"
        [pageLinks]="5"
        [first]="first"
        [totalRecords]="filteredScans.length"
        [paginator]="true"
        [draggable]="false"
        [loading]="isLoading()"
        (onPage)="onPage($event, filteredScans)">
        <!-- Header -->
        <ng-template pTemplate="header"
          class="overflow-auto h-full">
          <div class="flex column-gap-2 w-full p-2">
            <div class="flex-grow-1">
              <medsurf-design-form-control-form [formGroup]="toolbarSettingsForm"
                [class]="'w-full'">
                <div class="flex flex-wrap gap-2">
                  <!-- All Profiles Toggle -->
                  <medsurf-design-form-control-input-switch [formField]="false"
                    [control]="toolbarSettingsForm.controls.allProfiles"
                    [label]="'medsurf-import-stats.show_all_profiles'"
                  [class]="'flex'"></medsurf-design-form-control-input-switch>
                  <!-- Search String -->
                  <medsurf-design-form-control-input-text [formField]="false"
                    [control]="toolbarSettingsForm.controls.searchString"
                    [placeholder]="'global.search_query'"
                    [autofocus]="true"
                    [class]="'flex-grow-1'"></medsurf-design-form-control-input-text>
                  <!-- AutoRefresh-Checkbox -->
                  <medsurf-design-form-control-input-checkbox [formField]="false"
                    [control]="toolbarSettingsForm.controls.autoRefresh"
                    [label]="'medsurf-import-stats.enable_auto_refresh'"
                  [class]="'flex'"></medsurf-design-form-control-input-checkbox>
                  <!-- Refresh-Button -->
                  <button medsurf-design-input-button [id]="'refresh_button'"
                    [label]="'global.refresh' | translate"
                    [iconClass]="isLoading() ? FlatModels.ButtonDesignModels.ButtonDefaultIcons.REFRESH : FlatModels.ButtonDesignModels.ButtonDefaultIcons.SPINNER"
                  (click)="refresh()"></button>
                </div>
              </medsurf-design-form-control-form>
            </div>
            <div class="flex flex-shrink-0 gap-2">
              <!-- Bulk Actions -->
              <medsurf-design-input-menu [formField]="false"
                [name]="'slides_menu'"
                [label]="'global.slides'"
              [loadMenuItems]="getSlidesMenuItems"></medsurf-design-input-menu>
              <!-- Layout Options -->
              <medsurf-design-content-sortable-data-view-layout-options [layout]="layout"></medsurf-design-content-sortable-data-view-layout-options>
            </div>
          </div>
          <div class="p-2">
            @if (layout === 'list') {
              <div class="table-head h-5rem pl-2 pr-3 overflow-hidden"
                >
                <div class="w-2rem">
                  &nbsp;
                </div>
                <div class="w-3rem flex-shrink-0">
                  &nbsp;
                </div>
                <div class="w-5rem flex-shrink-0">
                  &nbsp;
                </div>
                <span translate="global.status"
                class="w-7rem"></span>
                <span translate="global.profile"
                class="w-4rem"></span>
                <span translate="global.name"
                class="w-20rem flex-grow-1"></span>
                <span translate="global.size"
                class="w-5rem"></span>
                <span translate="global.log"
                class="w-15rem"></span>
                <span translate="global.uploaded_at"
                class="w-10rem"></span>
                <span translate="medsurf-import-stats.converted_planes"
                class="w-6rem"></span>
                <div class="w-4rem flex-shrink-0">
                  &nbsp;
                </div>
                <div class="w-4rem flex-shrink-0">
                  &nbsp;
                </div>
              </div>
            }
          </div>
        </ng-template>
        <!-- List Item -->
        <ng-template pTemplate="listItem"
          let-statsData
          let-rowIndex="rowIndex">
          @if ({
            hasBulkEditPage: (nodeSharedFacade.hasBulkEditPage$(statsData.slideId) | async) || false
          }; as pageData) {
            <div class="table-row h-5rem pl-2 cursor-pointer overflow-hidden hover:bg-primary-200"
              [ngClass]="{'bg-primary-300': pageData.hasBulkEditPage}"
              (click)="onPageClick($event, statsData)">
              <div class="w-2rem">
                {{ first + rowIndex + 1 }}
              </div>
              <div class="w-3rem flex-shrink-0">
                <medsurf-design-input-checkbox [formField]="false"
                  [name]="'page_list_select_' + statsData.slideId"
                  [model]="pageData.hasBulkEditPage"
                (valueChange)="onToggleBulkPage(statsData.slideId)"></medsurf-design-input-checkbox>
              </div>
              <div class="w-5rem flex-shrink-0">
                @if ({
                  media: slideAuthorFacade.media$(statsData.media[0]?.id) | async
                }; as mediaData) {
                  <img
                    class="block h-4rem w-4rem bg-black-alpha-30"
                    [src]="mediaControlService.getUrl(mediaData.media, 'thumbnail') || '/assets/images/no-image.png'"
                    [alt]="mediaData.media?.name || statsData.name" />
                }
              </div>
              <div class="w-7rem">
                <p-tag 
                  [value]="`enum.importStatus.${statsData.status}` | translate"
                  [severity]="getStatusSeverityClass(statsData.status)" />
              </div>
              <div class="w-4rem">
                {{ statsData.profile || 'unknown' }}
              </div>
              <div class="w-20rem flex-grow-1 text-overflow-ellipsis-2">
                {{ statsData.name }}
              </div>
              <div class="w-5rem text-overflow-ellipsis">
                {{ statsData.size | fileSize: 1 }}
              </div>
              <div class="w-15rem text-overflow-ellipsis-2">
                @if (statsData.logs.length > 0) {
                  {{ getM((statsData.logs | sortBy_shared:'desc':'ts')[0]) || '-' }}
                }
              </div>
              <div class="w-10rem text-overflow-ellipsis-2">
                {{ statsData.lastModified | date_shared }}
              </div>
              <div class="w-6rem">
                {{ statsData.media?.length || '-' }}
              </div>
              <div class="w-4rem flex-shrink-0">
                @if (statsData.profile === userProfile && statsData.slideId && statsData.status === FlatModels.ImportStatsModels.StatusSeverity.enum.finished) {
                  <button medsurf-design-input-button
                    [id]="'pages_linking_' +  statsData.slideId"
                    [title]="'global.pages_linking' | translate"
                    [iconClass]="FlatModels.ButtonDesignModels.ButtonDefaultIcons.ATTACH"
                    [inputClass]="FlatModels.ButtonDesignModels.ButtonDesignClass.PRIMARY"
                  (click)="attachPage(statsData.slideId)"></button>
                }
              </div>
              <div class="w-4rem flex-shrink-0">
                @if (statsData.profile === userProfile && statsData.slideId) {
                  <button medsurf-design-input-button
                    [id]="'open_preview_' + statsData.slideId"
                    [title]="'global.preview' | translate"
                    [iconClass]="FlatModels.ButtonDesignModels.ButtonDefaultIcons.PREVIEW"
                    [inputClass]="FlatModels.ButtonDesignModels.ButtonDesignClass.PRIMARY"
                  (click)="goToSlide(statsData.slideId)"></button>
                }
              </div>
            </div>
          }
        </ng-template>
        <!-- Grid Item -->
        <ng-template pTemplate="gridItem"
          let-statsData>
          @if ({
            hasBulkEditPage: (nodeSharedFacade.hasBulkEditPage$(statsData.slideId) | async) || false
          }; as pageData) {
            <div class="flex flex-column w-12rem h-16rem border-1 border-round cursor-pointer overflow-hidden hover:border-primary-200 hover:bg-primary-200"
              [ngClass]="{'bg-primary-300': pageData.hasBulkEditPage}"
              [title]="statsData.name"
              (click)="onPageClick($event, statsData)">
              <div class="relative flex w-12rem h-12rem">
                <p-tag
                  [value]="`enum.importStatus.${statsData.status}` | translate"
                  [class]="'absolute top-0 left-0 m-2'"
                  [severity]="getStatusSeverityClass(statsData.status)"
                  />
                <medsurf-design-input-checkbox
                  [formField]="false"
                  [name]="'page_grid_select_' + statsData.slideId"
                  [model]="pageData.hasBulkEditPage"
                  [class]="'h-3rem absolute top-0 right-0'"
                  (valueChange)="onToggleBulkPage(statsData.slideId)"
                ></medsurf-design-input-checkbox>
                <medsurf-design-content-chip [tooltipLabel]="'global.profile' | translate"
                  [class]="'absolute bottom-0 left-0 m-2'">
                  {{ statsData.profile || 'unknown' }}
                </medsurf-design-content-chip>
                <div class="absolute bottom-0 right-0 m-2 flex gap-1">
                  @if (statsData.profile === userProfile && statsData.slideId && statsData.status === FlatModels.ImportStatsModels.StatusSeverity.enum.finished) {
                    <button medsurf-design-input-button
                      [id]="'pages_linking_' + statsData.slideId"
                      [title]="'global.pages_linking' | translate"
                      [iconClass]="FlatModels.ButtonDesignModels.ButtonDefaultIcons.ATTACH"
                      [inputClass]="FlatModels.ButtonDesignModels.ButtonDesignClass.PRIMARY_GRID_BUTTON"
                    (click)="attachPage(statsData.slideId)"></button>
                  }
                  @if (statsData.profile === userProfile && statsData.slideId) {
                    <button medsurf-design-input-button
                      [id]="'open_preview_' + statsData.slideId"
                      [title]="'global.preview' | translate"
                      [iconClass]="FlatModels.ButtonDesignModels.ButtonDefaultIcons.PREVIEW"
                      [inputClass]="FlatModels.ButtonDesignModels.ButtonDesignClass.PRIMARY_GRID_BUTTON"
                    (click)="goToSlide(statsData.slideId)"></button>
                  }
                </div>
                @if ({
                  media: slideAuthorFacade.media$(statsData.media[0]?.id) | async
                }; as mediaData) {
                  <img
                    class="w-full bg-black-alpha-30"
                    [src]="mediaControlService.getUrl(mediaData.media, 'thumbnail') || '/assets/images/no-image.png'"
                    [alt]="mediaData.media?.name || 'global.error_loading_image' | translate" />
                }
              </div>
              <div class="flex w-12rem align-items-start h-4rem p-2">
                <span class="overflow-hidden text-overflow-ellipsis-2">
                  {{ statsData.name }}
                </span>
              </div>
            </div>
          }
        </ng-template>
      </medsurf-design-content-sortable-data-view>
    }
  </div>
</div>